import ArrowLink from 'components/ArrowLink';
import IconLink from 'components/IconLink';
import { PriceText } from 'components/Price';
import RichText from 'components/RichText';
import Table from 'components/Table';
import Text from 'components/Text';
import type { CreditSimulationCostCalculation } from 'models/api';
import type { SitecoreLink } from 'models/sitecore';
import type { HTMLString } from 'types';
import { formatTemplate } from 'utils/format';

function formatSekkiLinkHref(
	sekkiLinkHref: string | undefined,
	amountToPay: number | undefined,
) {
	if (!amountToPay || !sekkiLinkHref) {
		return undefined;
	}
	const href = formatTemplate(sekkiLinkHref, { amountToPay });
	if (href.includes('amountToPay')) {
		return undefined;
	}
	return href;
}

interface Props {
	amountToPay: number | undefined;
	applicationLink: SitecoreLink | undefined;
	content?: HTMLString;
	costCalculations: CreditSimulationCostCalculation[];
	example: string;
	heading: string;
	interestRateText: string;
	invoiceFeeText: string;
	monthlyCostText: string;
	repaymentTimeText: string;
	sekkiLink: SitecoreLink | undefined;
	startupFeeText: string;
	tableHeading: string;
	termsLink: SitecoreLink | undefined;
	totalAmountText: string;
}

export default function CreditSimulation({
	amountToPay,
	applicationLink,
	content,
	costCalculations,
	example,
	heading,
	interestRateText,
	invoiceFeeText,
	monthlyCostText,
	repaymentTimeText,
	sekkiLink,
	startupFeeText,
	tableHeading,
	termsLink,
	totalAmountText,
}: Props) {
	const sekkiLinkHref = formatSekkiLinkHref(sekkiLink?.href, amountToPay);
	return (
		<>
			<p className="mb-2 font-bold">{heading}</p>
			{content && (
				<RichText className="mb-8" html={content} checkMarkListType="boxed" />
			)}

			<Text className="mb-4" as="p">
				{tableHeading}
			</Text>
			<Table
				rows={[
					{
						className: 'bg-brand text-inverse',
						cells: [
							{
								isHeader: true,
								content: repaymentTimeText,
							},
							...costCalculations
								.filter((calc) => calc.key)
								.map((calc) => ({
									isHeader: true,
									content: calc.key,
								})),
						],
					},
					{
						cells: [
							{
								content: monthlyCostText,
							},
							...costCalculations
								.filter((calc) => calc.monthlyCost)
								.map((calc) => ({
									content: <PriceText price={calc.monthlyCost} />,
								})),
						],
					},
					{
						cells: [
							{
								content: startupFeeText,
							},
							...costCalculations
								.filter((calc) => calc.startupFee)
								.map((calc) => ({
									content: <PriceText price={calc.startupFee} />,
								})),
						],
					},
					{
						cells: [
							{
								content: invoiceFeeText,
							},
							...costCalculations
								.filter((calc) => calc.invoiceFee)
								.map((calc) => ({
									content: <PriceText price={calc.invoiceFee} />,
								})),
						],
					},
					{
						cells: [
							{
								content: interestRateText,
							},
							...costCalculations
								.filter((calc) => calc.effectiveInterestRate)
								.map((calc) => ({
									content: calc.effectiveInterestRate,
								})),
						],
					},
					{
						cells: [
							{
								content: totalAmountText,
							},
							...costCalculations
								.filter((calc) => calc.totalAmount)
								.map((calc) => ({
									content: <PriceText price={calc.totalAmount} />,
								})),
						],
					},
				]}
			/>
			{termsLink?.href && termsLink.text && (
				<IconLink
					className="mt-4 flex"
					target={termsLink.target}
					href={termsLink.href}
					text={termsLink.text}
					iconPosition="right"
					icon="open-in-new"
				/>
			)}
			{sekkiLink?.text && sekkiLinkHref && (
				<IconLink
					className="mt-4 flex"
					target={sekkiLink.target}
					href={sekkiLinkHref}
					text={sekkiLink.text}
					iconPosition="right"
					icon="open-in-new"
				/>
			)}
			{applicationLink?.href && applicationLink.text && (
				<ArrowLink
					className="mt-6"
					target={applicationLink.target}
					href={applicationLink.href}
					text={applicationLink.text}
				/>
			)}
			<Text as="pSmall" className="mt-4 mb-2">
				{example}
			</Text>
		</>
	);
}
CreditSimulation.displayName = 'CreditSimulation';
